import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import HomeIcon from '@mui/icons-material/Home';
import GavelIcon from '@mui/icons-material/Gavel';
import SensorsIcon from '@mui/icons-material/Sensors';
import FilterListIcon from '@mui/icons-material/FilterList';
import PageShell from '../components/PageShell';
import ProductCard from '../components/ProductCard';
import CustomerNav from '../components/CustomerNav';
import TermsDialog from '../components/TermsDialog';
import PrivacySummaryStep from '../components/PrivacySummaryStep';
import AuthCard from '../components/AuthCard';
import CustomerStatusBar from '../components/CustomerStatusBar';
import request from '../api';
import { formatDateTime } from '../dateUtils';
import { PRODUCT_TYPES } from '../productTypes';

const RESEND_COOLDOWN_MS = 60 * 1000;

function VerifyEmailStep({ username, onVerified }) {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [resendUntil, setResendUntil] = useState(0);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!resendUntil) return undefined;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [resendUntil]);

    const cooldownRemaining = Math.max(0, resendUntil - now);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setNotice('');
        setBusy(true);
        try {
            const data = await request('/customer/verify-email', {
                method: 'POST',
                body: { username, code },
            });
            localStorage.setItem('customerToken', data.token);
            onVerified(data.token, data.customer);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        setError('');
        setNotice('');
        try {
            await request('/customer/resend-verification', {
                method: 'POST',
                body: { username },
            });
            setNotice('Código reenviado. Revisa tu correo.');
            setResendUntil(Date.now() + RESEND_COOLDOWN_MS);
            setNow(Date.now());
        } catch (err) {
            if (err.code === 'RATE_LIMITED' && err.retryAfterMs) {
                setResendUntil(Date.now() + err.retryAfterMs);
                setNow(Date.now());
            }
            setError(err.message);
        }
    };

    return (
        <AuthCard>
            <Typography variant="h6" gutterBottom>Verifica tu Correo Electrónico</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Te enviamos un código de verificación de 6 dígitos. Ingrésalo para activar tu cuenta.
            </Typography>
            <Box component="form" onSubmit={submit}>
                <Stack spacing={2}>
                    <TextField
                        label="Código de Verificación"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        fullWidth
                        required
                        autoFocus
                        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    />
                    <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
                        Verificar
                    </Button>
                    {error && <Alert severity="error">{error}</Alert>}
                    {notice && <Alert severity="success">{notice}</Alert>}
                    <Button onClick={resend} color="inherit" disabled={cooldownRemaining > 0}>
                        {cooldownRemaining > 0
                            ? `Reenviar código (${Math.ceil(cooldownRemaining / 1000)}s)`
                            : 'Reenviar código'}
                    </Button>
                </Stack>
            </Box>
        </AuthCard>
    );
}

function AuthBox({ onAuthed }) {
    const [mode, setMode] = useState('register');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);
    const [pendingUsername, setPendingUsername] = useState('');

    const isRegister = mode === 'register';

    const submit = async (e) => {
        e.preventDefault();
        if (isRegister && !termsAccepted) {
            setError('Debes aceptar los Términos y Condiciones para registrarte');
            return;
        }
        setError('');
        setBusy(true);
        try {
            const data = await request(`/customer/${mode === 'login' ? 'login' : 'register'}`, {
                method: 'POST',
                body: isRegister ? { username, email, password } : { username, password },
            });
            if (isRegister) {
                setPendingUsername(data.customer.username);
            } else {
                localStorage.setItem('customerToken', data.token);
                onAuthed(data.token, data.customer);
            }
        } catch (err) {
            if (err.code === 'EMAIL_NOT_VERIFIED') {
                setPendingUsername(err.username || username);
                return;
            }
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    if (pendingUsername) {
        return <VerifyEmailStep username={pendingUsername} onVerified={onAuthed} />;
    }

    return (
        <AuthCard>
            <Tabs value={mode} onChange={(e, v) => setMode(v)} variant="fullWidth" sx={{ mb: 3 }}>
                <Tab label="Registrarse" value="register" />
                <Tab label="Iniciar Sesión" value="login" />
            </Tabs>
            <Box component="form" onSubmit={submit}>
                <Stack spacing={2}>
                    <TextField
                        label="Usuario"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        fullWidth
                        required
                        autoFocus
                    />
                    {isRegister && (
                        <TextField
                            label="Correo Electrónico"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            required
                        />
                    )}
                    <TextField
                        label="Contraseña"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        required
                    />
                    {isRegister && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={termsAccepted}
                                    onChange={(e) => setTermsAccepted(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    He leído y acepto los{' '}
                                    <Link
                                        component="button"
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setTermsOpen(true);
                                        }}
                                    >
                                        Términos y Condiciones
                                    </Link>
                                </Typography>
                            }
                        />
                    )}
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={busy || (isRegister && !termsAccepted)}
                        fullWidth
                    >
                        {mode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
                    </Button>
                    {error && <Alert severity="error">{error}</Alert>}
                    {mode === 'login' && (
                        <Link component={RouterLink} to="/forgot-password" variant="body2">
                            ¿Olvidaste tu contraseña?
                        </Link>
                    )}
                </Stack>
            </Box>
            <TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
        </AuthCard>
    );
}

function ForcedPasswordChange({ token, onChanged }) {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (newPassword !== confirmPassword) {
            setError('La nueva contraseña y su confirmación no coinciden');
            return;
        }
        setBusy(true);
        try {
            const updated = await request('/customer/password', {
                method: 'PATCH',
                token,
                body: { current_password: currentPassword, new_password: newPassword },
            });
            onChanged(updated);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('customerToken');
        window.location.href = '/auctions';
    };

    return (
        <AuthCard>
            <Typography variant="h6" gutterBottom>Debes Cambiar tu Contraseña</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Un administrador restableció tu contraseña. Ingresa la contraseña temporal que te
                compartió y elige una nueva para continuar.
            </Typography>
            <Box component="form" onSubmit={submit}>
                <Stack spacing={2}>
                    <TextField
                        label="Contraseña Temporal"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        fullWidth
                        required
                        autoFocus
                    />
                    <TextField
                        label="Nueva Contraseña"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                        required
                    />
                    <TextField
                        label="Confirmar Nueva Contraseña"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                        required
                    />
                    <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
                        Actualizar Contraseña
                    </Button>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Button onClick={logout} color="inherit">Cerrar Sesión</Button>
                </Stack>
            </Box>
        </AuthCard>
    );
}

function CustomerPortal() {
    const [token, setToken] = useState(localStorage.getItem('customerToken'));
    const [customer, setCustomer] = useState(null);
    const [products, setProducts] = useState([]);
    const [checkingAuth, setCheckingAuth] = useState(!!localStorage.getItem('customerToken'));
    const [privacyBusy, setPrivacyBusy] = useState(false);
    const [privacyError, setPrivacyError] = useState('');
    const [filterOpen, setFilterOpen] = useState(false);
    const [selectedTypes, setSelectedTypes] = useState(PRODUCT_TYPES);
    const [draftTypes, setDraftTypes] = useState(PRODUCT_TYPES);

    const loadProducts = useCallback((tok) => {
        request('/products', { token: tok }).then(setProducts).catch(() => {});
    }, []);

    const loadMe = useCallback(
        (tok) => {
            request('/customer/me', { token: tok })
                .then((cust) => {
                    setCustomer(cust);
                    loadProducts(tok);
                })
                .catch(() => {
                    localStorage.removeItem('customerToken');
                    setToken(null);
                    setCustomer(null);
                })
                .finally(() => setCheckingAuth(false));
        },
        [loadProducts]
    );

    useEffect(() => {
        if (token) {
            loadMe(token);
        } else {
            setCheckingAuth(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleAuthed = (tok, cust) => {
        setToken(tok);
        setCustomer(cust);
        loadProducts(tok);
    };

    const isFilterActive = selectedTypes.length !== PRODUCT_TYPES.length;
    const filteredProducts = isFilterActive
        ? products.filter((item) => selectedTypes.includes(item.product_type))
        : products;

    const openFilter = () => {
        setDraftTypes(selectedTypes);
        setFilterOpen(true);
    };

    const toggleDraftType = (type) => {
        setDraftTypes((prev) =>
            prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
        );
    };

    const applyFilter = () => {
        setSelectedTypes(draftTypes);
        setFilterOpen(false);
    };

    const acceptPrivacySummary = async () => {
        setPrivacyError('');
        setPrivacyBusy(true);
        try {
            await request('/customer/privacy-summary-seen', { method: 'PATCH', token });
            setCustomer((prev) => ({ ...prev, privacy_summary_seen: true }));
        } catch (err) {
            setPrivacyError(err.message);
        } finally {
            setPrivacyBusy(false);
        }
    };

    if (checkingAuth) {
        return (
            <PageShell title="PlayBid" nav={null}>
                <Typography align="center">Cargando...</Typography>
            </PageShell>
        );
    }

    const nav = customer && !customer.must_change_password && customer.privacy_summary_seen ? (
        <CustomerNav />
    ) : customer ? null : (
        <Button component={RouterLink} to="/" color="inherit" startIcon={<HomeIcon />}>Volver al Inicio</Button>
    );

    return (
        <PageShell title="PlayBid" nav={nav}>
            {customer && customer.must_change_password ? (
                <ForcedPasswordChange token={token} onChanged={setCustomer} />
            ) : customer && !customer.privacy_summary_seen ? (
                <PrivacySummaryStep
                    onAccept={acceptPrivacySummary}
                    busy={privacyBusy}
                    error={privacyError}
                />
            ) : customer ? (
                <>
                    <CustomerStatusBar customer={customer} />

                    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                        <Button
                            variant={isFilterActive ? 'contained' : 'outlined'}
                            color={isFilterActive ? 'primary' : 'inherit'}
                            startIcon={<FilterListIcon />}
                            onClick={openFilter}
                        >
                            {isFilterActive ? `Filtrar (${selectedTypes.length})` : 'Filtrar'}
                        </Button>
                    </Stack>

                    <Grid
                        container
                        spacing={3}
                        sx={{ mb: { xs: 'calc(72px + env(safe-area-inset-bottom))', sm: '84px' } }}
                    >
                        {filteredProducts.map((item) => (
                            <Grid key={item.id} item xs={12} sm={6} md={4}>
                                <ProductCard
                                    to={`/products/${item.id}`}
                                    image={item.image_url}
                                    title={item.title}
                                    subtitle={`Vendido por ${item.store_name}`}
                                    storeLogoUrl={item.store_logo_url}
                                    storeName={item.store_name}
                                    meta={
                                        <Chip
                                            size="small"
                                            label={`Finaliza: ${formatDateTime(item.termination_time)}`}
                                            sx={{ bgcolor: 'rgba(15,23,42,0.06)' }}
                                        />
                                    }
                                />
                            </Grid>
                        ))}
                        {filteredProducts.length === 0 && products.length > 0 && (
                            <Grid item xs={12}>
                                <Paper
                                    sx={{
                                        p: { xs: 4, sm: 6 },
                                        textAlign: 'center',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'rgba(123,47,247,0.1)',
                                            border: '1px solid rgba(123,47,247,0.35)',
                                            mb: 1,
                                        }}
                                    >
                                        <FilterListIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                    </Box>
                                    <Typography variant="h6">Ninguna publicación coincide con tu filtro</Typography>
                                    <Typography color="text.secondary" sx={{ maxWidth: 440 }}>
                                        Ajusta los tipos de producto seleccionados para ver más subastas disponibles.
                                    </Typography>
                                    <Button
                                        onClick={() => setSelectedTypes(PRODUCT_TYPES)}
                                        variant="outlined"
                                        color="inherit"
                                        startIcon={<FilterListIcon />}
                                        sx={{ mt: 1 }}
                                    >
                                        Quitar Filtro
                                    </Button>
                                </Paper>
                            </Grid>
                        )}
                        {products.length === 0 && (
                            <Grid item xs={12}>
                                <Paper
                                    sx={{
                                        p: { xs: 4, sm: 6 },
                                        textAlign: 'center',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'rgba(123,47,247,0.1)',
                                            border: '1px solid rgba(123,47,247,0.35)',
                                            mb: 1,
                                        }}
                                    >
                                        <GavelIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                    </Box>
                                    <Typography variant="h6">Por ahora no hay subastas disponibles</Typography>
                                    <Typography color="text.secondary" sx={{ maxWidth: 440 }}>
                                        Ya se remataron todos los artículos. Mantente al pendiente: pronto llegarán
                                        nuevos productos, y no olvides que puedes participar en las subastas en
                                        tiempo real que organizan tus tiendas favoritas.
                                    </Typography>
                                    <Button
                                        component={RouterLink}
                                        to="/room/join"
                                        variant="outlined"
                                        color="inherit"
                                        startIcon={<SensorsIcon />}
                                        sx={{ mt: 1 }}
                                    >
                                        Unirme a una Subasta en Vivo
                                    </Button>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>

                    <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} fullWidth maxWidth="xs">
                        <DialogTitle>Filtrar por Tipo de Producto</DialogTitle>
                        <DialogContent>
                            <FormGroup>
                                {PRODUCT_TYPES.map((type) => (
                                    <FormControlLabel
                                        key={type}
                                        control={
                                            <Checkbox
                                                checked={draftTypes.includes(type)}
                                                onChange={() => toggleDraftType(type)}
                                            />
                                        }
                                        label={type}
                                    />
                                ))}
                            </FormGroup>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setFilterOpen(false)}>Cancelar</Button>
                            <Button variant="contained" onClick={applyFilter}>Aplicar</Button>
                        </DialogActions>
                    </Dialog>
                </>
            ) : (
                <>
                    <Typography align="center" color="text.secondary" sx={{ mb: 2 }}>
                        Inicia sesión o regístrate como cliente para ver las subastas disponibles.<br></br>
                        No requieres tarjeta de crédito para participar.
                    </Typography>
                    <AuthBox onAuthed={handleAuthed} />
                </>
            )}
        </PageShell>
    );
}

export default CustomerPortal;
